import { createStore } from '@/shared/lib/store';
import type { AttributeFilter } from '@/entities/dashboards';
import type { StrategistTargetAttribute } from '@/entities/strategist';

/**
 * Выборка (scope), в контексте которой открыт список диалогов ячейки.
 * Совпадает по смыслу со сквозным фильтром страницы раздела «Стратег».
 */
export type DialogsDrawerScope = {
  /** Атрибутные фильтры выборки */
  filters: AttributeFilter[];
  /** Начало периода в формате YYYY-MM-DD */
  dateFrom?: string;
  /** Конец периода в формате YYYY-MM-DD */
  dateTo?: string;
  /** Целевая метрика страницы (по умолчанию — удержание) */
  target?: StrategistTargetAttribute;
};

/**
 * Спецификация открываемого среза: его адрес (хотя бы одно из
 * `objectionId` / `argumentId` / `conceptId` / `block`), заголовок и выборка.
 */
export type DialogsDrawerSpec = {
  /** conceptId понятия-возражения (строка матрицы) */
  objectionId?: string;
  /** conceptId понятия-аргумента (столбец матрицы возражение×аргумент) */
  argumentId?: string;
  /** conceptId понятия любой роли (строка иерархии) */
  conceptId?: string;
  /** Код блока стандарта (столбец матрицы стандартов) */
  block?: string;
  /** Порог схлопывания возражения (медоид раскрывается в кластер) — как в матрице */
  objectionThreshold?: number;
  /** Порог схлопывания аргумента (медоид раскрывается в кластер) — как в матрице */
  argumentThreshold?: number;
  /** Порог схлопывания понятия строки иерархии (медоид раскрывается в кластер) */
  conceptThreshold?: number;
  /**
   * Срез — сам переход «возражение → следующий аргумент», а не ячейка матрицы:
   * проваливание из рейтинга переходов показывает диалоги и показатели той же строки,
   * из которой открыто.
   */
  transition?: boolean;
  /** Заголовок дровера — человекочитаемое имя среза */
  title: string;
  /** Выборка страницы, в контексте которой открыт срез */
  scope: DialogsDrawerScope;
};

/**
 * Состояние стора дровера диалогов по ячейке.
 */
type DialogsDrawerStore = {
  /** Открытая ячейка (spec) либо null — дровер закрыт */
  spec: DialogsDrawerSpec | null;
  /** Открыть дровер диалогов для ячейки */
  open: (spec: DialogsDrawerSpec) => void;
  /** Закрыть дровер */
  close: () => void;
};

/**
 * Общий стор «проваливания в диалоги по срезу» раздела «Стратег».
 *
 * Клик по ячейке любой матрицы или по строке иерархии вызывает
 * {@link DialogsDrawerStore.open} со спецификацией среза; смонтированный на
 * странице `DialogsDrawerContent` подписан на стор, грузит диалоги среза и
 * показывает влияние и их список.
 */
export const useDialogsDrawerStore = createStore<DialogsDrawerStore>('dialogs-drawer', (set) => ({
  spec: null,
  open: (spec) => set({ spec }),
  close: () => set({ spec: null }),
}));
